/**
 * Metronome — quarter-note click track driven by Transport BPM.
 *
 * Deliberately routed **directly to `ctx.destination`, not through
 * `engine.master`** — the Recorder taps `engine.master`, so keeping
 * the metronome outside that path guarantees click sounds never end
 * up in exported WAV files. The user hears it; Ableton doesn't.
 *
 *            ┌── voiceBus → effects → master ──► ctx.destination
 *            │                         │
 *            │                         └── Recorder tap
 * Metronome ─┴──────────────────────────► ctx.destination   (bypass)
 *
 * Scheduling uses the classic Web Audio "lookahead" pattern: every
 * 25 ms a JS interval fires and schedules any click whose onset is
 * within the next 100 ms via `setValueAtTime` / `setTargetAtTime` on
 * the oscillator's gain envelope. Because scheduling happens in
 * real audio time (not JS time), clicks land sample-accurately even
 * if the JS thread stalls briefly.
 *
 * Downbeat differentiation: every Nth click (4 by default) uses a
 * higher pitch (1800 Hz vs 1200 Hz) so the user hears bar starts.
 */

import type { AudioEngine } from './AudioEngine';
import type { Transport } from './Transport';

/** How often the scheduler wakes up to post upcoming clicks. */
const LOOKAHEAD_MS = 25;
/** How far ahead (in seconds) clicks get scheduled on each tick. */
const LOOKAHEAD_SEC = 0.1;
/** Tone envelope — super short so clicks read as percussive ticks. */
const CLICK_ATTACK_SEC = 0.002;
const CLICK_DECAY_SEC = 0.05;
/** Beats in a bar — downbeat gets the accent pitch. */
const BEATS_PER_BAR = 4;

export type MetronomeListener = (enabled: boolean) => void;

export class Metronome {
  private engine: AudioEngine | null = null;
  private transport: Transport | null = null;
  private enabled = false;

  /** 0..1 user-facing volume. Cached so setVolume works before attach,
   *  and re-applied whenever the outGain is (re)created. */
  private volume = 0.4;
  /** Output gain — user-facing volume control, routed to destination. */
  private outGain: GainNode | null = null;
  /** Running count of clicks fired; `beatsInBar=0` hits are downbeats. */
  private beatCount = 0;
  /** Audio-time of the next scheduled click. */
  private nextClickTime = 0;
  /** setInterval handle for the scheduler; null when stopped. */
  private schedulerHandle: ReturnType<typeof setInterval> | null = null;

  private readonly listeners = new Set<MetronomeListener>();

  attach(engine: AudioEngine, transport: Transport): void {
    this.engine = engine;
    this.transport = transport;
  }

  getEnabled(): boolean {
    return this.enabled;
  }

  subscribe(fn: MetronomeListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Toggle on/off. Safe to call before attach — just caches intent; the
   * first attach that runs while enabled will start the scheduler.
   */
  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    if (on) this.start();
    else this.stop();
    for (const fn of this.listeners) fn(on);
  }

  toggle(): void {
    this.setEnabled(!this.enabled);
  }

  /**
   * Read current volume (0..1). Handy for UI initialization — lets
   * the slider paint its initial position from a single source of
   * truth.
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Update click volume. Clamped to [0, 1]. Writes through to the
   * live GainNode with a tiny time constant so mid-run drags don't
   * pop. Safe to call before attach — the cached value is applied
   * the first time `outGain` is created.
   */
  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this.volume = clamped;
    const engine = this.engine;
    if (!engine || !this.outGain) return;
    const ctx = engine.context;
    const t = ctx.currentTime;
    this.outGain.gain.cancelScheduledValues(t);
    this.outGain.gain.setTargetAtTime(clamped, t, 0.01);
  }

  /** Start (or restart) the click scheduler from the current audio time. */
  private start(): void {
    const engine = this.engine;
    if (!engine) return;
    const ctx = engine.context;

    if (!this.outGain) {
      this.outGain = ctx.createGain();
      // Use the cached volume so a setVolume before start() takes
      // effect the moment the output node comes online.
      this.outGain.gain.value = this.volume;
      // Bypass master entirely so the Recorder tap can't see this.
      this.outGain.connect(ctx.destination);
    }

    // Anchor the first click slightly in the future so scheduling has
    // headroom even on the very first tick.
    this.nextClickTime = ctx.currentTime + 0.05;
    this.beatCount = 0;

    // Fire the scheduler immediately so short taps of the button still
    // produce an audible click.
    this.scheduleUpcoming();
    this.schedulerHandle = setInterval(
      () => this.scheduleUpcoming(),
      LOOKAHEAD_MS,
    );
  }

  /** Stop the scheduler. Already-scheduled clicks still fire (they're on
   *  audio-thread time) but no new ones get queued. */
  private stop(): void {
    if (this.schedulerHandle !== null) {
      clearInterval(this.schedulerHandle);
      this.schedulerHandle = null;
    }
  }

  /**
   * Peek ahead `LOOKAHEAD_SEC` from `ctx.currentTime` and schedule any
   * clicks whose onset falls inside that window. Advances `beatCount`
   * per click and recomputes the next onset from the live BPM (so
   * tempo changes take effect immediately).
   */
  private scheduleUpcoming(): void {
    const engine = this.engine;
    const transport = this.transport;
    if (!engine || !transport || !this.outGain) return;
    const ctx = engine.context;
    const horizon = ctx.currentTime + LOOKAHEAD_SEC;
    while (this.nextClickTime < horizon) {
      this.scheduleClick(this.nextClickTime);
      const beatSec = 60 / transport.bpm.value;
      this.nextClickTime += beatSec;
      this.beatCount++;
    }
  }

  /**
   * Fire a single click at `when`. Downbeats (every Nth beat) get a
   * brighter accent pitch; regular beats use the muted base pitch.
   * One-shot oscillator + gain envelope — nodes are scoped to this
   * click and garbage-collect after they stop.
   */
  private scheduleClick(when: number): void {
    const engine = this.engine;
    if (!engine || !this.outGain) return;
    const ctx = engine.context;
    const isDownbeat = this.beatCount % BEATS_PER_BAR === 0;
    const freq = isDownbeat ? 1800 : 1200;
    const peak = isDownbeat ? 1.0 : 0.7;

    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, when);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(peak, when + CLICK_ATTACK_SEC);
    // Exponential-ish decay via setTargetAtTime; stops the node just
    // after it's inaudible so the audio graph stays tidy.
    env.gain.setTargetAtTime(0, when + CLICK_ATTACK_SEC, CLICK_DECAY_SEC / 3);

    osc.connect(env);
    env.connect(this.outGain);
    osc.start(when);
    osc.stop(when + CLICK_ATTACK_SEC + CLICK_DECAY_SEC + 0.05);
  }

  dispose(): void {
    this.stop();
    try {
      this.outGain?.disconnect();
    } catch {
      /* already disconnected */
    }
    this.outGain = null;
    this.listeners.clear();
  }
}
